import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { i18n } from "../../translate/i18n";
import {
  scoreColor,
  scoreBadgeLabel,
  temperatureColor,
  outcomeColor,
  stageLabel,
  temperatureLabel,
  outcomeLabel,
  formatBRL,
  formatShortDateTime,
} from "../../utils/saleAudit";

const OUTCOMES = ["ganhou", "negociando", "perdeu", "sem_venda"];

// Chaves aceitas pelo backend (GROUP_BY_MAP); "" = sem agrupamento.
const GROUP_OPTIONS = ["user", "connection", "day"];

// Chave de dia vem como "YYYY-MM-DD" (string, sem timezone) — só reordena.
const formatGroupDay = (key) => {
  const [year, month, day] = String(key).split("-");
  return day && month && year ? `${day}/${month}/${year}` : String(key);
};

// Direção sensata ao trocar de coluna: nota começa pelas piores (asc);
// datas começam pelo mais recente (desc).
const DEFAULT_ORDER_DIR = {
  score: "asc",
  firstMessageAt: "desc",
  lastMessageAt: "desc",
};

const sortAriaLabel = (dir) =>
  dir === "desc"
    ? i18n.t("saleAudits.table.sortDescending")
    : i18n.t("saleAudits.table.sortAscending");

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 16,
    marginBottom: theme.spacing(2),
  },
  row: {
    cursor: "pointer",
  },
  score: {
    fontWeight: 800,
    fontSize: 26,
    lineHeight: 1.1,
  },
  scoreCell: {
    width: 88,
  },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  groupRow: {
    backgroundColor: theme.palette.action.hover,
  },
  groupCell: {
    fontWeight: 700,
  },
  groupMeta: {
    marginLeft: theme.spacing(1),
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
}));

const outlinedChip = (label, color) => (
  <Chip
    size="small"
    variant="outlined"
    label={label}
    style={{ borderColor: color, color, fontWeight: 600, height: 22 }}
  />
);

// Lista de auditorias (filtros por conexão/resultado, tabela, paginação).
// Usada tanto na página dedicada (/sale-audits) quanto na aba "Atendimentos"
// do dashboard — mantida aqui para não duplicar a tabela em dois lugares.
const SaleAuditList = () => {
  const classes = useStyles();
  const history = useHistory();

  const [whatsApps, setWhatsApps] = useState([]);
  const [whatsappTab, setWhatsappTab] = useState("");
  const [outcome, setOutcome] = useState("");
  const [orderBy, setOrderBy] = useState("score");
  const [orderDir, setOrderDir] = useState(DEFAULT_ORDER_DIR.score);
  const [groupBy, setGroupBy] = useState("");

  const [audits, setAudits] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        setWhatsApps(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Filtro, ordenação ou agrupamento mudou → recomeça da página 1.
  useEffect(() => {
    setAudits([]);
    setPageNumber(1);
  }, [whatsappTab, outcome, orderBy, orderDir, groupBy]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrderDir(DEFAULT_ORDER_DIR[field]);
    }
  };

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageNumber, orderBy, orderDir };
      if (whatsappTab) params.whatsappId = whatsappTab;
      if (outcome) params.outcome = outcome;
      if (groupBy) params.groupBy = groupBy;
      const { data } = await api.get("/sale-audits", { params });
      const list = data?.audits || [];
      setAudits((prev) => (pageNumber === 1 ? list : [...prev, ...list]));
      // Totais de cada grupo são do conjunto filtrado inteiro (vêm do
      // backend); contar as linhas já carregadas mostraria só a página.
      setGroups(Array.isArray(data?.groups) ? data.groups : []);
      setHasMore(Boolean(data?.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [whatsappTab, outcome, orderBy, orderDir, groupBy, pageNumber]);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits, refreshKey]);

  // Nova auditoria → recarrega da primeira página (debounced).
  useEffect(() => {
    const socket = openSocket();
    const bump = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setAudits([]);
        setPageNumber(1);
        setRefreshKey((k) => k + 1);
      }, 1500);
    };
    socket.on("saleAudit", bump);
    return () => {
      socket.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Lookup por chave do grupo. Contagem e nota média vêm do backend (conjunto
  // filtrado inteiro); somar as linhas carregadas mostraria só a página atual.
  const groupsByKey = new Map(
    (groups || []).map((g) => [g && g.key !== undefined ? g.key : null, g]),
  );

  const groupAt = (key) => groupsByKey.get(key === undefined ? null : key);

  const groupHeaderLabel = (key) => {
    const group = groupAt(key);
    const raw = group && group.label !== undefined ? group.label : key;
    if (raw === null || raw === undefined || raw === "") {
      return groupBy === "user" ? i18n.t("saleAudits.groups.noUser") : "—";
    }
    return groupBy === "day" ? formatGroupDay(raw) : String(raw);
  };

  const groupHeaderSummary = (key) => {
    const group = groupAt(key);
    if (!group) return "";
    if (group.avgScore === null || group.avgScore === undefined) {
      return i18n.t("saleAudits.groups.summaryNoScore", { count: group.count });
    }
    return i18n.t("saleAudits.groups.summary", {
      count: group.count,
      avgScore: group.avgScore,
    });
  };

  return (
    <>
      <Paper variant="outlined" style={{ marginBottom: 16 }}>
        <Tabs
          value={whatsappTab}
          onChange={(e, v) => setWhatsappTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={i18n.t("saleAudits.tabs.all")} value="" />
          {whatsApps.map((w) => (
            <Tab key={w.id} label={w.name} value={String(w.id)} />
          ))}
        </Tabs>
      </Paper>

      <div className={classes.toolbar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel shrink htmlFor="sale-audits-outcome-filter">
            {i18n.t("saleAudits.filters.outcome")}
          </InputLabel>
          <Select
            native
            label={i18n.t("saleAudits.filters.outcome")}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            inputProps={{ id: "sale-audits-outcome-filter" }}
          >
            <option value="">{i18n.t("saleAudits.filters.all")}</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {outcomeLabel(o)}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel shrink htmlFor="sale-audits-group-by">
            {i18n.t("saleAudits.groups.label")}
          </InputLabel>
          <Select
            native
            label={i18n.t("saleAudits.groups.label")}
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            inputProps={{ id: "sale-audits-group-by" }}
          >
            <option value="">{i18n.t("saleAudits.groups.none")}</option>
            {GROUP_OPTIONS.map((g) => (
              <option key={g} value={g}>
                {i18n.t(`saleAudits.groups.${g}`)}
              </option>
            ))}
          </Select>
        </FormControl>
      </div>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                className={classes.scoreCell}
                sortDirection={orderBy === "score" ? orderDir : false}
              >
                <TableSortLabel
                  active={orderBy === "score"}
                  direction={orderBy === "score" ? orderDir : "asc"}
                  onClick={() => handleSort("score")}
                  aria-label={sortAriaLabel(
                    orderBy === "score" ? orderDir : "asc",
                  )}
                >
                  {i18n.t("saleAudits.table.score")}
                </TableSortLabel>
              </TableCell>
              <TableCell>{i18n.t("saleAudits.table.contact")}</TableCell>
              <TableCell>{i18n.t("saleAudits.table.user")}</TableCell>
              <TableCell>{i18n.t("saleAudits.table.stage")}</TableCell>
              <TableCell>{i18n.t("saleAudits.table.temperature")}</TableCell>
              <TableCell align="right">
                {i18n.t("saleAudits.table.potentialValue")}
              </TableCell>
              <TableCell>{i18n.t("saleAudits.table.outcome")}</TableCell>
              <TableCell>
                {i18n.t("saleAudits.table.suggestedAction")}
              </TableCell>
              <TableCell
                sortDirection={orderBy === "firstMessageAt" ? orderDir : false}
              >
                <TableSortLabel
                  active={orderBy === "firstMessageAt"}
                  direction={orderBy === "firstMessageAt" ? orderDir : "desc"}
                  onClick={() => handleSort("firstMessageAt")}
                  aria-label={sortAriaLabel(
                    orderBy === "firstMessageAt" ? orderDir : "desc",
                  )}
                >
                  {i18n.t("saleAudits.table.firstContact")}
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "lastMessageAt" ? orderDir : false}
              >
                <TableSortLabel
                  active={orderBy === "lastMessageAt"}
                  direction={orderBy === "lastMessageAt" ? orderDir : "desc"}
                  onClick={() => handleSort("lastMessageAt")}
                  aria-label={sortAriaLabel(
                    orderBy === "lastMessageAt" ? orderDir : "desc",
                  )}
                >
                  {i18n.t("saleAudits.table.lastContact")}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audits.map((audit, index) => {
              // Quebra de grupo: a lista já vem ordenada pela chave do grupo,
              // então basta comparar com a linha anterior. Backend sem
              // groupKey (versão antiga) → tabela normal, sem cabeçalho.
              const showGroupHeader =
                Boolean(groupBy) &&
                audit.groupKey !== undefined &&
                (index === 0 || audits[index - 1].groupKey !== audit.groupKey);

              return (
                <React.Fragment key={audit.id}>
                  {showGroupHeader && (
                    <TableRow className={classes.groupRow}>
                      <TableCell colSpan={10} className={classes.groupCell}>
                        {groupHeaderLabel(audit.groupKey)}
                        <Typography
                          component="span"
                          variant="caption"
                          className={classes.groupMeta}
                        >
                          {groupHeaderSummary(audit.groupKey)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    hover
                    className={classes.row}
                    onClick={() =>
                      history.push("/sale-audits/" + audit.ticketId)
                    }
                  >
                    <TableCell align="center" className={classes.scoreCell}>
                      <span
                        className={classes.score}
                        style={{ color: scoreColor(audit.score) }}
                      >
                        {audit.score}
                      </span>
                      <Typography
                        variant="caption"
                        display="block"
                        style={{
                          color: scoreColor(audit.score),
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        {scoreBadgeLabel(audit.score)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {audit.ticket?.contact?.name || "—"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {audit.ticket?.contact?.number || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>{audit.user?.name || "—"}</TableCell>
                    <TableCell>
                      {outlinedChip(stageLabel(audit.stage), "#6B7280")}
                    </TableCell>
                    <TableCell>
                      {/* Temperatura do lead segue visível, mas discreta: a tela
                          agora é de qualidade de atendimento, não de venda. */}
                      <Typography
                        variant="caption"
                        style={{ color: temperatureColor(audit.temperature) }}
                      >
                        {temperatureLabel(audit.temperature)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatBRL(audit.potentialValue)}
                    </TableCell>
                    <TableCell>
                      {outlinedChip(
                        outcomeLabel(audit.outcome),
                        outcomeColor(audit.outcome),
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {audit.suggestedAction || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatShortDateTime(audit.firstMessageAt)}
                    </TableCell>
                    <TableCell>
                      {formatShortDateTime(audit.lastMessageAt)}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {!loading && audits.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("saleAudits.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {hasMore && (
          <div className={classes.loadMore}>
            <Button
              variant="outlined"
              color="primary"
              disabled={loading}
              onClick={() => setPageNumber((p) => p + 1)}
            >
              {i18n.t("saleAudits.buttons.loadMore")}
            </Button>
          </div>
        )}
      </Paper>
    </>
  );
};

export default SaleAuditList;
